import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { PolicyGetResponse, PolicyTermSearchResponse } from '@lio/api-types';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { addDays, isBefore, isSameDay, startOfDay } from 'date-fns';

import { jam } from '@jam.dev/sdk';

import {
  PolicyTransaction,
  PolicyTransactionStatus,
  UNDONE_TRANSACTION_STATUSES,
  PolicyTransactionTypes,
  EndorsementPolicyDialogType,
  PolicyDialogType,
  PolicyCancelationMethod,
  PolicyCancelationReason,
  PolicyCancelationSource,
  RenewDialogType,
} from '@models/policyTransaction';
import {
  useCancelationReturnPremiumEstimate,
  useCreatePolicyTransaction,
  useDisassociateConditionalRenewalQuoteId,
  useUpdatePolicyTransaction,
} from '@services/policy';
import { formatCurrency } from '@utils/number';
import { ToastType } from '@components/toast/ToastType.enum';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { Form } from '@components/forms/Form';
import { FormSection } from '@components/forms/FormSection.js';
import { useToastStore } from '@store/Toast.js';
import { SequenceEndorsementDialog } from '@pages/PolicyDetails/dialogs/index';
import { getOutOfSequenceBlockingTransactions } from '@pages/PolicyDetails/dialogs/utils/getOutOfSequenceBlockingTransactions';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { Dialog } from '@components/dialog/Dialog.js';

import { Item } from '@components/select';
import { Checkbox } from '@components/checkbox/Checkbox';
import Loading from '@components/Loading';
import { setDateToMidnight } from '@utils/index';
import { fakeLocalMidnightFromTZMidnight } from '@utils/date';

import {
  getPolicyTransactionDialogData,
  getCreatePolicyTransactionRequestFromFormData,
  checkIfTransactionCreateIsValid,
  getUpdatePolicyTransactionRequestFromFormData,
  ProducerFieldProps,
  COMPLIANCE_DAYS_ERROR_MESSAGE,
  isLegalNoticeDaysRequirementMet,
} from './utils/index.js';
import { getCancelationGrounds } from './utils/cancelationGrounds';
import { companyOptions } from './PolicyTransactionDialog.constants';
import { writingCompanyIds } from '@models/productOffering';
import * as styles from './NewTransactionDialog.styles';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { formatFormValues } from '@components/forms/formatFormValues.js';

export interface CreatePolicyTransactionBaseData {
  effectiveOn: Date | null;
}

type CreatePolicyTransactionFormValues = {
  effectiveOn?: Date | null;
  grounds1?: string;
  grounds2?: string;
  grounds3?: string;
  reason?: string;
  effectiveDate?: Date;
  [key: string]: unknown;
};

export interface CreatePolicyTransactionDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  isDialogLoading?: boolean;
  policyTransactionsError?: boolean;
  onRetryPolicyTransactions?: () => void;
  policy: PolicyGetResponse;
  selectedPolicyTransaction?: PolicyTransaction;
  policyTransactions: PolicyTransaction[];
  policyTerms: PolicyTermSearchResponse['items'];
  /** True if the policyTerms fetch failed. An empty array is otherwise indistinguishable from "no terms exist". */
  policyTermsError?: boolean;
  onRetryPolicyTerms?: () => void;
  dialogType: PolicyDialogType | EndorsementPolicyDialogType | RenewDialogType;
  other?: {
    billingPlanOptions?: Item[];
    /** Used to override the button text for the PolicyTransaction creation */
    overrideCreateButtonText?: string;
    /** last processed transaction effective date */
    lastProcessedEffectiveDate?: Date;
    /** last processed cancellation effective date */
    lastCancelationEffectiveDate?: Date;
    /** If true will show the confirmation dialog rather than a form */
    showConfirmationDialog?: boolean;
    /** producer field */
    producerField?: ProducerFieldProps;
    /** renewal quote id for conditional renewal payload */
    renewalQuoteId?: string;
    /** id of an existing processed conditional renewal transaction tied to this quote's renewal — used for Mark for Nonrenewal */
    conditionalRenewalTransactionId?: string;
  };
}

const doNotRedirectTypes = [
  PolicyTransactionTypes.ManageRoles,
  PolicyTransactionTypes.BillingPlanEndorsement,
  PolicyTransactionTypes.Cancelation,
  PolicyTransactionTypes.Reinstatement,
  PolicyTransactionTypes.DoNotRenew,
  PolicyTransactionTypes.RescindDoNotRenew,
  PolicyTransactionTypes.ConditionalRenewal,
  'Rollback',
];

export const CreatePolicyTransactionDialog = ({
  isOpen,
  onCancel,
  policy,
  policyTerms,
  policyTermsError,
  onRetryPolicyTerms,
  selectedPolicyTransaction,
  policyTransactions,
  isDialogLoading,
  policyTransactionsError,
  onRetryPolicyTransactions,
  dialogType,
  other,
}: CreatePolicyTransactionDialogProps) => {
  const { isMobileScreen } = useGetScreenSize();
  const [{ isAdmin, isPortalAdmin, isUnderwriter, isSrOps }] = useStore((state) => [state.userAuth], shallow);
  const { enablePersistToast } = useToastStore();
  const navigate = useNavigate();

  const [deleteConditionalRenewalOnNonrenewal, setDeleteConditionalRenewalOnNonrenewal] = useState(false);
  const deleteConditionalRenewalOnNonrenewalRef = useRef(deleteConditionalRenewalOnNonrenewal);
  deleteConditionalRenewalOnNonrenewalRef.current = deleteConditionalRenewalOnNonrenewal;

  const methods = useForm<CreatePolicyTransactionFormValues>({
    defaultValues: {},
    mode: 'onChange',
  });

  const isValid = methods.formState.isValid;

  const watchedReason = methods.watch('reason');
  const watchedEffectiveDate = methods.watch('effectiveDate');
  const watchedMethod = methods.watch('method');
  const watchedSourceField = methods.watch('source');

  // Mirrors the source derivation in getCreatePolicyTransactionRequestFromFormData:
  // the `source` field is only shown/collected when reason === Other, otherwise it's
  // implied by whether the reason is a company- or insured-initiated one.
  const derivedCancelationSource = useMemo(() => {
    if (dialogType !== PolicyDialogType.Cancelation || !watchedReason) return undefined;
    const reason = watchedReason as PolicyCancelationReason;
    if (reason === PolicyCancelationReason.Other) {
      return (watchedSourceField as PolicyCancelationSource | undefined) ?? undefined;
    }
    const isCompanyReason = companyOptions.map((option) => option.value as PolicyCancelationReason).includes(reason);
    return isCompanyReason ? PolicyCancelationSource.Company : PolicyCancelationSource.Insured;
  }, [dialogType, watchedReason, watchedSourceField]);

  const isUndoneTransaction = UNDONE_TRANSACTION_STATUSES.includes(selectedPolicyTransaction?.status ?? '');

  // Estimates only apply to a new, editable Cancelation -- not one already
  // processed and opened read-only from Transaction History, where the real
  // OneShield-confirmed amount already exists and submission has happened.
  const isCancelationReadOnly =
    dialogType === PolicyDialogType.Cancelation &&
    (selectedPolicyTransaction?.status === PolicyTransactionStatus.Processed || isUndoneTransaction);

  const cancelationEstimateInput = useMemo(() => {
    if (dialogType !== PolicyDialogType.Cancelation || isCancelationReadOnly) return null;
    const method = watchedMethod as PolicyCancelationMethod | undefined;
    const isValidEffectiveDate =
      watchedEffectiveDate instanceof Date && !isNaN((watchedEffectiveDate as Date).getTime());
    if (!method || !derivedCancelationSource || !isValidEffectiveDate) return null;
    return {
      method,
      source: derivedCancelationSource,
      cancellationEffectiveOn: setDateToMidnight(watchedEffectiveDate as Date),
    };
  }, [dialogType, isCancelationReadOnly, watchedMethod, derivedCancelationSource, watchedEffectiveDate]);

  const {
    estimate: cancelationEstimate,
    isPending: isCancelationEstimatePending,
    isError: isCancelationEstimateError,
  } = useCancelationReturnPremiumEstimate(policy.id, cancelationEstimateInput);

  const companyDecisionLegalNoticeDays = policy.legalNoticeDays ?? null;
  const isCompanyDecisionNoticeError =
    watchedReason === PolicyCancelationReason.CompanyRequest &&
    watchedEffectiveDate instanceof Date &&
    !isNaN(watchedEffectiveDate.getTime()) &&
    companyDecisionLegalNoticeDays != null &&
    companyDecisionLegalNoticeDays > 0 &&
    isBefore(watchedEffectiveDate, addDays(startOfDay(new Date()), companyDecisionLegalNoticeDays));

  // Flat is only a valid cancelation method on the effective date of a term-start
  // transaction (New Business, New Business Rewrite, or Renewal) -- never mid-term.
  const termStartDates = useMemo(() => {
    if (dialogType !== PolicyDialogType.Cancelation) return [];
    const termStartTransactionTypes: string[] = [
      PolicyTransactionTypes.NewBusiness,
      PolicyTransactionTypes.NewBusinessRewrite,
      PolicyTransactionTypes.Renewal,
    ];
    return (policyTerms ?? [])
      .filter((term) => termStartTransactionTypes.includes(term.policyTransactionType))
      .map((term) => fakeLocalMidnightFromTZMidnight(new Date(term.effectiveOn)));
  }, [dialogType, policyTerms]);

  const isEffectiveDateAtTermStart =
    dialogType !== PolicyDialogType.Cancelation ||
    (watchedEffectiveDate instanceof Date &&
      !isNaN(watchedEffectiveDate.getTime()) &&
      termStartDates.some((termStartDate) => isSameDay(termStartDate, watchedEffectiveDate as Date)));

  const isFlatOnNonTermStartError =
    dialogType === PolicyDialogType.Cancelation &&
    watchedMethod === PolicyCancelationMethod.Flat &&
    watchedEffectiveDate instanceof Date &&
    !isNaN(watchedEffectiveDate.getTime()) &&
    !isEffectiveDateAtTermStart;

  const showConfirmationDialog =
    dialogType === RenewDialogType.RescindDoNotRenew || dialogType === PolicyDialogType.Rescindment;

  const legalNoticeDaysRequirementMet = isLegalNoticeDaysRequirementMet(policy);
  const isLegalNoticeDaysBlocked =
    (dialogType === RenewDialogType.DoNotRenew || dialogType === RenewDialogType.ConditionalRenewal) &&
    !legalNoticeDaysRequirementMet;

  const selectedConditionalGrounds = {
    grounds1: methods.watch('grounds1'),
    grounds2: methods.watch('grounds2'),
    grounds3: methods.watch('grounds3'),
  };

  const { enqueueSnackbar } = useSnackbar();

  const [cancelationGroundsOptions, setCancelationGroundsOptions] = useState<Item[]>([]);
  useEffect(() => {
    if (dialogType !== PolicyDialogType.Cancelation) return;
    const writingCompany = policy?.boundQuote?.writingCompany?.id === writingCompanyIds.specialty ? 'LSIC' : 'LIC';
    getCancelationGrounds(policy, writingCompany)
      .then((grounds) => {
        setCancelationGroundsOptions(grounds.map((ground) => ({ value: ground, label: ground })));
      })
      .catch(() => {
        enqueueSnackbar('Grounds for cancelation could not be loaded', { variant: ToastType.Error });
      });
  }, [dialogType, enqueueSnackbar, policy]);

  const showCannotCreateDialog = useMemo(() => {
    // if the transaction is already processed (or undone) we will just open it for details - not create a new one so skip this check
    if (selectedPolicyTransaction?.status === PolicyTransactionStatus.Processed || isUndoneTransaction) {
      return null;
    }
    return checkIfTransactionCreateIsValid({ policy, onCancel, dialogType, navigate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogType, policy, onCancel]);

  const {
    title,
    fields: rawFields,
    transactionType,
    buttonConfig,
    toastConfig,
  } = useMemo(() => {
    return getPolicyTransactionDialogData({
      dialogType,
      policy,
      policyTerms,
      policyTransaction: selectedPolicyTransaction,
      other: {
        ...other,
        selectedConditionalGrounds,
        cancelationGroundsOptions,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dialogType,
    policy,
    selectedPolicyTransaction,
    isDialogLoading,
    other,
    other?.billingPlanOptions,
    other?.billingPlanOptions?.length,
    other?.producerField?.brokerageProducers,
    methods.formState.isValid,
    selectedConditionalGrounds?.grounds1,
    selectedConditionalGrounds?.grounds2,
    selectedConditionalGrounds?.grounds3,
    cancelationGroundsOptions,
  ]);

  const fields = useMemo(() => {
    let updatedFields = rawFields;

    if (isCompanyDecisionNoticeError && rawFields.effectiveDate) {
      updatedFields = {
        ...updatedFields,
        effectiveDate: {
          ...updatedFields.effectiveDate,
          error: true,
          helperText:
            'The Number of Days notice to meet compliance has not been met. Either select a day that is further out, or extend the expiration date of the policy',
        },
      };
    }

    if (dialogType === PolicyDialogType.Cancelation && updatedFields.method) {
      const methodOptions = (updatedFields.method.fieldConfig?.selectField?.options ?? []).map((option) =>
        option.value === PolicyCancelationMethod.Flat ? { ...option, disabled: !isEffectiveDateAtTermStart } : option,
      );
      updatedFields = {
        ...updatedFields,
        method: {
          ...updatedFields.method,
          fieldConfig: {
            ...updatedFields.method.fieldConfig,
            selectField: {
              ...updatedFields.method.fieldConfig?.selectField,
              options: methodOptions,
            },
          },
          ...(isFlatOnNonTermStartError && {
            error: true,
            helperText:
              'Flat is only allowed on the effective date of a New Business, New Business Rewrite, or Renewal transaction.',
          }),
        },
      };
    }

    return updatedFields;
  }, [rawFields, isCompanyDecisionNoticeError, dialogType, isEffectiveDateAtTermStart, isFlatOnNonTermStartError]);

  const { updatePolicyTransaction, isPending: isUpdatePolicyTransactionLoading } = useUpdatePolicyTransaction();
  const { createPolicyTransaction, isPending: isCreatePolicyTransactionLoading } = useCreatePolicyTransaction();
  const { disassociateConditionalRenewalQuoteId } = useDisassociateConditionalRenewalQuoteId();

  const [sequenceDialogDate, setSequenceDialogDate] = React.useState<Date | undefined>(undefined);
  const isAuthorizedForOose = isAdmin || isPortalAdmin || isUnderwriter || isSrOps;

  const isReadOnly =
    selectedPolicyTransaction?.status === PolicyTransactionStatus.Processed ||
    isUndoneTransaction ||
    dialogType === RenewDialogType.ConditionalRenewalView ||
    dialogType === RenewDialogType.DoNotRenewView;

  useEffect(() => {
    if (!isOpen) {
      methods.reset({});
      setDeleteConditionalRenewalOnNonrenewal(false);
    }
  }, [isOpen, methods]);

  // jam metadata
  jam.metadata(() => {
    return {
      timeSincePageLoad: performance.now(),
      propInfo: {
        billingPlanOptions: (other?.billingPlanOptions ?? []) as any,
      },
      formData: {
        fields: Object.keys(fields).reduce(
          (acc, key) => {
            acc[key] = { ...fields[key] };
            return acc;
          },
          {} as Record<string, any>,
        ),
      },
    };
  });

  const handleBeforeSubmit = () => {
    const effectiveOnValue = (methods.getValues('effectiveOn') as Date | null | undefined) ?? null;

    if (
      effectiveOnValue === null ||
      (transactionType !== PolicyTransactionTypes.Endorsement &&
        transactionType !== PolicyTransactionTypes.BillingPlanEndorsement)
    ) {
      return true;
    }

    const effectiveOnDate = setDateToMidnight(effectiveOnValue);

    const blockingTransactions = getOutOfSequenceBlockingTransactions({
      policyTransactions,
      effectiveOnDate,
    });

    const isOOSE = blockingTransactions.length > 0;

    if (!isOOSE) {
      return true;
    }

    if (!isAuthorizedForOose) {
      // unauthorized users cannot create out-of-sequence endorsements
      enqueueSnackbar(
        'Out-of-sequence endorsements require Underwriter or Admin access. Please contact your underwriter.',
        { variant: ToastType.Error, persist: true },
      );
      return false;
    }

    setSequenceDialogDate(effectiveOnDate);
    return false;
  };

  const isModified = methods.formState.isDirty;

  const dialogControls = useMemo(() => {
    if (dialogType === RenewDialogType.ConditionalRenewalView || dialogType === RenewDialogType.DoNotRenewView) {
      return {
        hideButton: true,
        cancelButtonText: 'Exit',
        confirmButtonText: undefined,
      };
    }

    // Undone transactions are view-only: there is nothing to create or continue.
    if (isUndoneTransaction) {
      return {
        hideButton: true,
        cancelButtonText: 'Exit',
        confirmButtonText: undefined,
      };
    }

    // TODO: consider moving to helper function to keep this component more readable
    let value;
    switch (selectedPolicyTransaction?.status) {
      case PolicyTransactionStatus.Created:
        value = {
          confirmButtonText: buttonConfig?.continueButtonText ?? `Continue ${transactionType}`,
          onSubmit: async () => {
            if (isModified) {
              const canContinue = handleBeforeSubmit();
              if (!canContinue) {
                return;
              }
              await handleSubmitUpdate();
            }
            const quoteId =
              selectedPolicyTransaction?.quote?.id ||
              (selectedPolicyTransaction as any)?.policyConditionalRenewalTransaction?.renewalQuoteId ||
              other?.renewalQuoteId;

            if (quoteId) {
              navigate(`/quote/${quoteId}`);
            }
          },
        };
        break;
      case PolicyTransactionStatus.Processed:
        value = {
          hideButton:
            buttonConfig?.hidePrimaryButton || transactionType === PolicyTransactionTypes.BillingPlanEndorsement,
          confirmButtonText: buttonConfig?.viewButtonText ?? `View ${transactionType}`,
          onSubmit: () => {
            navigate(`/quote/${selectedPolicyTransaction?.quote?.id}`);
          },
        };
        break;
      default:
        value = {
          hideButton: buttonConfig?.hidePrimaryButton,
          cancelButtonText: buttonConfig?.cancelButtonText ?? `Exit`,
          confirmButtonText: buttonConfig?.createButtonText ?? `Create ${transactionType}`,
          onSubmit: async () => {
            const canContinue = handleBeforeSubmit();
            if (!canContinue) {
              return;
            }
            await handleSubmit();
          },
        };
        break;
    }

    return value;
    // eslint-disable-next-line
  }, [dialogType, selectedPolicyTransaction, isModified, isOpen, policyTransactions]);

  const handleExitDialog = () => {
    onCancel();
  };

  const handleSubmit = async () => {
    const formData = methods.getValues();
    const formatted = formatFormValues(formData, fields, { visibleOnly: true });

    const policyTransactionRequest = getCreatePolicyTransactionRequestFromFormData({
      transactionType,
      policy,
      formData: formatted,
      renewalQuoteId: other?.renewalQuoteId,
    });

    await createPolicyTransaction(policyTransactionRequest, {
      onError: () => {
        enqueueSnackbar(toastConfig?.errorMessage ?? `${transactionType} of Policy failed`, {
          variant: ToastType.Error,
          persist: true,
        });

        handleExitDialog();
      },
      onSuccess: async (res, variables) => {
        // When an OoSE endorsement is created, navigate to the endorsement quote first so the
        // user can fill it out and get it to Ready to Bind. The resolution wizard is entered
        // after the endorsement itself is bound (BindQuotePage handles that redirect).
        // The ooseResolutionId is present in the response but we intentionally fall through to
        // the normal quote navigation below.

        // @ts-ignore
        if (!doNotRedirectTypes.includes(transactionType)) {
          if (toastConfig?.successMessage) {
            enablePersistToast();
            enqueueSnackbar(toastConfig?.successMessage, {
              variant: ToastType.Success,
            });
          }

          navigate(`/quote/${res?.quoteId}`);
        } else {
          if (res.status !== variables.status) {
            enqueueSnackbar(toastConfig?.errorMessage ?? `${transactionType} of Policy failed`, {
              variant: ToastType.Error,
              persist: true,
            });
          } else if (toastConfig?.successMessage) {
            enqueueSnackbar(toastConfig?.successMessage, {
              variant: ToastType.Success,
            });
          }

          // Handle conditional renewal side effect for Mark for Nonrenewal
          if (transactionType === PolicyTransactionTypes.DoNotRenew && other?.conditionalRenewalTransactionId) {
            if (deleteConditionalRenewalOnNonrenewalRef.current) {
              await updatePolicyTransaction(
                {
                  policyTransactionId: other.conditionalRenewalTransactionId,
                  body: { status: PolicyTransactionStatus.Canceled },
                },
                {
                  onError: async (e) => {
                    const message = await getApiErrorMessage(e, 'Failed to delete Conditional Renewal');
                    enqueueSnackbar(message, {
                      variant: ToastType.Error,
                      persist: true,
                    });
                  },
                },
              ).catch(() => {
                // Error toast is handled above; swallow rejection so the main flow can complete.
              });
            } else {
              await disassociateConditionalRenewalQuoteId(other.conditionalRenewalTransactionId).catch(async (e) => {
                const message = await getApiErrorMessage(e, 'Failed to disassociate Conditional Renewal');
                enqueueSnackbar(message, {
                  variant: ToastType.Error,
                  persist: true,
                });
              });
            }
          }

          handleExitDialog();
        }
      },
    });
  };

  const handleSubmitUpdate = async () => {
    const formData = methods.getValues();

    const updateRequest = getUpdatePolicyTransactionRequestFromFormData({
      transactionType,
      formData,
      policyTransaction: selectedPolicyTransaction!,
      policy,
    });

    await updatePolicyTransaction(updateRequest, {
      onError: () => {
        enqueueSnackbar(toastConfig?.errorMessage ?? `${transactionType} of Policy failed`, {
          variant: ToastType.Error,
          persist: true,
        });

        handleExitDialog();
      },
      onSuccess: () => {
        if (doNotRedirectTypes.includes(transactionType)) {
          if (toastConfig?.successMessage) {
            enqueueSnackbar(toastConfig?.successMessage, {
              variant: ToastType.Success,
            });
          }

          handleExitDialog();
        }
      },
    });
  };

  const submitDisabled = useMemo(() => {
    return (
      isDialogLoading ||
      isUpdatePolicyTransactionLoading ||
      isCreatePolicyTransactionLoading ||
      isCompanyDecisionNoticeError ||
      isFlatOnNonTermStartError
    );
  }, [
    isDialogLoading,
    isCreatePolicyTransactionLoading,
    isUpdatePolicyTransactionLoading,
    isCompanyDecisionNoticeError,
    isFlatOnNonTermStartError,
  ]);

  const formSectionDisabled = useMemo(() => {
    // note: do not disable on isCompanyDecisionNoticeError; which is for the date picker where too soon of a date needs to be changed
    return isDialogLoading || isUpdatePolicyTransactionLoading || isCreatePolicyTransactionLoading;
  }, [isDialogLoading, isCreatePolicyTransactionLoading, isUpdatePolicyTransactionLoading]);

  if (policyTransactionsError) {
    return (
      <Dialog
        title='Unable to load policy transactions'
        description='The policy transaction history is required to determine whether this transaction is out of sequence.'
        open={isOpen}
        handleCloseDialog={handleExitDialog}
        primaryBtnAction={onRetryPolicyTransactions}
        primaryBtnText='Retry'
        secondaryBtnAction={handleExitDialog}
        secondaryBtnText='Exit'
      />
    );
  }

  if (policyTermsError && dialogType === PolicyDialogType.Cancelation) {
    // Term-start dates come from policyTerms; an unavailable response is
    // otherwise indistinguishable from "no terms exist", which would silently
    // disable Flat everywhere with no explanation. Block instead of guessing.
    return (
      <Dialog
        title='Unable to load policy term history'
        description='Policy term history is required to determine which cancellation effective dates are eligible for the Flat method.'
        open={isOpen}
        handleCloseDialog={handleExitDialog}
        primaryBtnAction={onRetryPolicyTerms}
        primaryBtnText='Retry'
        secondaryBtnAction={handleExitDialog}
        secondaryBtnText='Exit'
      />
    );
  }

  if (showCannotCreateDialog) {
    return (
      <Dialog
        title={showCannotCreateDialog.title}
        description={<Box sx={styles.dialogContent}>{showCannotCreateDialog.DialogContent}</Box>}
        open={isOpen}
        handleCloseDialog={handleExitDialog}
        secondaryBtnAction={handleExitDialog}
        secondaryBtnText='Exit'
      />
    );
  }

  if (showConfirmationDialog) {
    return (
      <ConfirmationDialog
        onCancel={handleExitDialog}
        isOpen={isOpen}
        isLoading={isUpdatePolicyTransactionLoading || isCreatePolicyTransactionLoading}
        description={title}
        buttonConfig={{
          confirmText: buttonConfig?.createButtonText ?? 'Yes',
          secondaryText: buttonConfig?.cancelButtonText ?? 'No',
          onConfirm: () => {
            if (transactionType === 'Rollback') {
              void handleSubmitUpdate();
              return;
            }

            if (dialogControls.onSubmit) {
              void dialogControls.onSubmit();
              return;
            }

            handleExitDialog();
          },
        }}
      />
    );
  }

  return (
    <>
      {!!sequenceDialogDate && (
        <SequenceEndorsementDialog
          isOpen
          policyTransactions={policyTransactions}
          handleClose={(confirmed) => {
            setSequenceDialogDate(undefined);
            if (confirmed) {
              void handleSubmit();
            }
          }}
          policy={policy}
          endorsementEffectiveDate={sequenceDialogDate}
        />
      )}
      <Dialog
        open={isOpen}
        sx={{ flex: 'none' }}
        title={title}
        fullScreen={isMobileScreen}
        handleCloseDialog={handleExitDialog}
        primaryBtnText={
          dialogControls.hideButton
            ? undefined // This causes button to not show (FUTURE: worth its own prop, probably)
            : other?.overrideCreateButtonText
              ? other?.overrideCreateButtonText
              : `${dialogControls.confirmButtonText}`
        }
        primaryBtnAction={dialogControls.onSubmit}
        isPrimaryBtnDisabled={submitDisabled || !isValid}
        isPrimaryBtnLoading={isUpdatePolicyTransactionLoading || isCreatePolicyTransactionLoading}
        isSecondaryBtnDisabled={formSectionDisabled} // should be able to exit even if submit is blocked due to bad date selected
        secondaryBtnText={buttonConfig?.cancelButtonText ?? 'Exit'}
        secondaryBtnAction={handleExitDialog}
      >
        {isOpen && (
          <Form methods={methods} formId='create-policy-transaction-form'>
            <Grid
              container
              spacing={0}
              sx={{
                gap: 2,
                mb: 3,
                padding: '06px 16px 0 16px',
              }}
            >
              {isDialogLoading ? (
                <Loading />
              ) : (
                <>
                  {dialogType === RenewDialogType.DoNotRenew && !isLegalNoticeDaysBlocked && (
                    <Box sx={{ pb: 1 }}>
                      Marking this policy for nonrenewal will prevent renewal transactions from being created or
                      continued on this policy.
                    </Box>
                  )}
                  {isLegalNoticeDaysBlocked && <Box sx={{ pb: 1 }}>{COMPLIANCE_DAYS_ERROR_MESSAGE}</Box>}
                  <FormSection
                    fields={fields}
                    styleConfig={{ spacing: 2 }}
                    isReadOnly={isReadOnly}
                    isDisabled={formSectionDisabled}
                  />
                  {dialogType === PolicyDialogType.Cancelation && !isCancelationReadOnly && (
                    <Box sx={{ width: '100%', mt: -1 }}>
                      {isCancelationEstimatePending && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant='bodySmall'>Calculating estimated return premium…</Typography>
                        </Box>
                      )}
                      {!isCancelationEstimatePending && isCancelationEstimateError && (
                        <Typography variant='bodySmall' sx={{ color: 'error.main' }}>
                          Unable to calculate an estimated return premium right now.
                        </Typography>
                      )}
                      {!isCancelationEstimatePending && !isCancelationEstimateError && cancelationEstimate && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant='bodySmall' fontWeight='bold'>
                              Estimated Return Premium
                            </Typography>
                            <Typography variant='bodySmall' fontWeight='bold'>
                              {formatCurrency(cancelationEstimate.returnPremium)}
                            </Typography>
                          </Box>
                          <Typography variant='caption' sx={{ opacity: 0.7 }}>
                            This is an estimate. The final amount will be determined by OneShield after submission.
                          </Typography>
                          <Typography variant='caption' sx={{ opacity: 0.7 }}>
                            Note: If data is already mismatched between Portal and OS, this estimate will be wrong.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {dialogType === RenewDialogType.DoNotRenew && !!other?.conditionalRenewalTransactionId && (
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }}>
                      <Checkbox
                        sx={{ marginLeft: -1 }}
                        checked={deleteConditionalRenewalOnNonrenewal}
                        onChange={(event) => setDeleteConditionalRenewalOnNonrenewal(event.target.checked)}
                      />
                      <Typography variant='bodySmall'>Delete the Conditional Renewal as well</Typography>
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Form>
        )}
      </Dialog>
    </>
  );
};
